import { Add, DownOutlined, Edit, Website } from "@payping/icons";
import { deleteClientAppOrToken, editClientRevoke, getClientAppList } from "@payping/redux/actions";
import { dateFormats, toUtc } from "@payping/utils/dateUtils";
import {
  Button,
  DataHeader,
  Dropdown,
  Flex,
  LegacyCardList,
  Modal,
  Space,
  TableAction,
  Typography,
  useMessagedAsync,
  usePaypingContext,
} from "@payping/web-components";
import { FC, useState } from "react";
import { DisplayModeEnum } from "@payping/web-components/legacyComponents/List/types";
import type { ListCardProps } from "@payping/web-components/legacyComponents/List/types";
import { useAppDispatch, useAppSelector } from "@/utils/redux";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { ConfirmModal, ConfirmModalProps } from "@/components/DashIpg/ConfirmModal";
import { OauthListEmpty } from "./OauthListEmpty";
import { TokenType } from "../Developers/Token/TokenTypes";
import OauthRenewModal from "./OauthRenewModal";
import { OauthFormModal } from "./OauthFormModal";

const OauthList: FC = () => {
  const { breakpoints } = usePaypingContext();
  const { lg } = breakpoints;
  const dispatch = useAppDispatch();
  const oauthList = useAppSelector((state) => state?.client?.app?.list);
  const { t } = useAppTranslation();
  const [modal, contextHolder] = Modal.useModal();
  const [formModalOpen, setFormModalOpen] = useState<boolean>(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false);
  const [renewModalOpen, setRenewModalOpen] = useState<boolean>(false);
  const [formModalData, setFormModalData] = useState<TokenType>({});
  const [confirmModalData, setConfirmModalData] = useState<ConfirmModalProps>();
  const [renewModalClientId, setRenewModalClientId] = useState<string>("");

  function showModal(token?: TokenType) {
    if (token) {
      setFormModalData({
        ...token,
        expireDate: toUtc({
          date: token.expireDate,
          dateFormat: dateFormats.standard.utcDateTime,
          toFormat: dateFormats.ui.utcDate,
        }).object,
      });
    } else {
      setFormModalData({});
    }
    setFormModalOpen(true);
  }

  const { loading } = useMessagedAsync(() => dispatch(getClientAppList()), [dispatch], { useProgress: true });

  const handleDelete = (clientId: string) => {
    modal.confirm({
      cancelText: t("cancel"),
      content: t("oauth.remove.confirm"),
      mask: { closable: true },
      okText: t("ok"),
      onOk: async () => {
        dispatch(deleteClientAppOrToken(clientId));
      },
    });
  };

  const handleRenew = (clientId: string) => {
    setRenewModalClientId(clientId);
    setRenewModalOpen(true);
  };

  const handleRevoke = (clientId: string) => {
    modal.confirm({
      cancelText: t("cancel"),
      content: t("oauth.revoke.attention"),
      mask: { closable: true },
      okText: t("ok"),
      onOk: async () => {
        await dispatch(editClientRevoke(clientId));
      },
    });
  };

  const data: ListCardProps["dataSource"] = oauthList?.map((oauth) => ({
    actions: [
      <a key={1}>
        <TableAction Icon={Edit} iconColor={"cta"} onClick={() => showModal(oauth)} label={t("edit")} />
      </a>,
      <Dropdown
        key={2}
        menu={{
          items: [
            {
              key: 0,
              label: t("oauth.renew.renewOauth"),
              onClick: () => handleRenew(oauth.clientId || ""),
            },
            {
              key: 1,
              label: t("oauth.revoke.revokeOauth"),
              onClick: () => handleRevoke(oauth.clientId || ""),
            },
            { key: 2, label: t("delete"), onClick: () => handleDelete(oauth.clientId || "") },
          ],
        }}
      >
        <a>
          <Space align={"center"}>
            {t("common.more")}
            <DownOutlined className="tw:block" width={10} />
          </Space>
        </a>
      </Dropdown>,
    ],
    descriptions: (
      <Flex gap={"small"}>
        <Website width={14} />
        <Typography.Text>
          {t("websiteAddress")} {oauth.clientUri}
        </Typography.Text>
      </Flex>
    ),

    key: oauth.clientId || "",
    title: {
      primary: (
        <Space>
          <Typography.Title level={5} className={"tw:font-normal"}>
            {oauth.clientName}
          </Typography.Title>

          <Typography.Text type={"secondary"}>
            {t("expire")}
            {oauth.expireMonths}
            {t("month")} {t("remains")}
          </Typography.Text>
        </Space>
      ),
    },
  }));
  return (
    <>
      <>
        <Space orientation={"vertical"} size={"middle"} className={"tw:w-full"}>
          <DataHeader
            title={
              <Space>
                <Typography.Title level={4}>{t("connections.oauth.title")}</Typography.Title>
                <Typography.Title type={"secondary"} level={4}>
                  ({t("connections.oauth.version")})
                </Typography.Title>
              </Space>
            }
            description={<Typography.Text>{t("connections.oauth.subTitle")}</Typography.Text>}
            primaryAction={
              <>
                {!loading && (oauthList || []).length === 0 ? <OauthListEmpty showModal={showModal} /> : null}
                {loading || oauthList?.length > 0 ? (
                  <Button key={0} type={"primary"} icon={<Add width={14} height={14} />} onClick={() => showModal()}>
                    {t("add.add")}
                  </Button>
                ) : null}
              </>
            }
          />
          {loading || oauthList?.length > 0 ? (
            <LegacyCardList
              loading={loading}
              className={"tw:w-full"}
              grid={{ xs: 1, sm: 1, md: 1, lg: 3, xl: 4, xxl: 4, xxxl: 4 }}
              dataSource={data}
              mode={lg ? DisplayModeEnum.List : DisplayModeEnum.Card}
              bordered={false}
            />
          ) : null}
        </Space>
      </>
      <OauthFormModal
        open={formModalOpen}
        setOpen={setFormModalOpen}
        setConfirmModalOpen={setConfirmModalOpen}
        formModalData={formModalData}
        setConfirmModalData={setConfirmModalData}
      />
      <OauthRenewModal
        open={renewModalOpen}
        setOpen={setRenewModalOpen}
        setConfirmModalOpen={setConfirmModalOpen}
        setConfirmModalData={setConfirmModalData}
        clientId={renewModalClientId}
      />
      <ConfirmModal open={confirmModalOpen} setOpen={setConfirmModalOpen} {...confirmModalData} />
      {contextHolder}
    </>
  );
};

export default OauthList;
